import React, { useEffect, useMemo, useRef, useState } from 'react';
import SelectedFacets from './components/FilterBar/SelectedFacets/SelectedFacets';
import FilterDrawer from './components/FilterDrawer/FilterDrawer';
import { FilterBar } from './components/FilterBar/FilterBar';
import Grid from '~/shared/components/Grid';
import { mq } from '~/lib/helpers/media-query.helper';
import { SSRPagination } from './components/Pagination/SSRPagination';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import { Configure, Index, useHits, usePagination } from 'react-instantsearch';
import PaginatedHits from './components/PaginatedHits/PaginatedHits';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import FilterList from './components/FilterList/FilterList';
import type { ParsedUrlQuery } from 'querystring';
import { MainAlgoliaIndex } from '~/lib/algolia';
import { useFrame } from '~/shared/utils';
import useCustomer from '../commerce-api/hooks/useCustomer';
import { usePage } from '~/templates/pages';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { _transformProductDetailsToDataLayer, getListName } from '~/shared/hooks/useEvents/helpers';
import { useInView } from 'react-intersection-observer';
import { GTM_LIST_ID } from '~/shared/hooks/useEvents/useGTMEvents/models';

type Props = {
    hitsPerPage?: number;
    filters?: string;
    listId: EventIdentifier;
    configureProps?: Record<string, any>;
    serverUrl?: string;
    query?: ParsedUrlQuery;
};

const ProductsLoader = ({ hitsPerPage = 20, listId, configureProps, serverUrl, query }: Props) => {
    const { breadcrumb } = usePage();
    const { isPlusMember } = useCustomer();
    const isMobile = useIsMobile();
    const { data: frame } = useFrame();
    const { productsImpressionsEvent } = useEvents(frame);
    const { hits } = useHits<TProductDetails>();
    const { currentRefinement: currentPage } = usePagination();
    const { inView, ref } = useInView({
        threshold: 1,
        rootMargin: '0px 0px -500px 0px',
        triggerOnce: true,
    });

    const filterBarWrapperRef = useRef<HTMLDivElement>(null);

    const [isEventTriggered, setIsEventTriggered] = useState(false);

    // hide facets on Gavekort PLP as they have only to distinct products
    const shouldHideFacets = useMemo(
        () => configureProps?.ruleContexts?.map((x: any) => x.toLowerCase()).includes('gavekort'),
        [configureProps?.ruleContexts],
    );

    useEffect(() => {
        if (isEventTriggered) return;
        if (hits.length && inView) {
            const eventType =
                listId === GTM_LIST_ID.SEARCH_RESULTS ? listId : GTM_LIST_ID.PRODUCT_LIST;
            const itemListName = breadcrumb ? getListName(breadcrumb) : '';
            const transformedItems = hits.map((hit, index) =>
                _transformProductDetailsToDataLayer(hit, currentPage, index, isPlusMember),
            );

            productsImpressionsEvent(eventType, itemListName, transformedItems);
            setIsEventTriggered(true);
        }
    }, [hits, isPlusMember, query, inView]);

    return (
        <>
            <div ref={ref}></div>
            <Configure
                {...configureProps}
                hitsPerPage={hitsPerPage}
                clickAnalytics
                getRankingInfo
            />

            {isMobile && <FilterDrawer />}

            {shouldHideFacets === false && (
                <div ref={filterBarWrapperRef}>
                    <FilterBar>
                        <FilterList />
                        <SelectedFacets />
                    </FilterBar>
                </div>
            )}

            <Index indexName={MainAlgoliaIndex} indexId="products">
                <PaginatedHits
                    listId={listId}
                    query={query?.query}
                    itemsPerPage={hitsPerPage}
                    filterBarRef={filterBarWrapperRef}
                />
            </Index>

            <FilterBar>
                <Grid
                    breakpoints={{
                        [mq('xs')]: {
                            flow: 'row',
                            rowGap: 'none',
                            columnGap: 'none',
                            columns: '1fr',
                        },
                        [mq('sm', 'md')]: {
                            columns: '1fr 1fr',
                            columnGap: 'small',
                            rowGap: 'small',
                        },
                        [mq('md', 'lg')]: {
                            columns: 'repeat(3, 1fr)',
                            columnGap: 'medium',
                            rowGap: 'medium',
                        },
                        [mq('lg')]: {
                            columns: 'repeat(4, 1fr)',
                            columnGap: 'medium',
                            rowGap: 'medium',
                        },
                    }}
                    columnGap="medium"
                    flow="column"
                />
            </FilterBar>

            <SSRPagination asPath={serverUrl} />
        </>
    );
};

export default ProductsLoader;
